import {
  googleAdSuitabilityForTitleId,
  isGoogleDemandUrl,
  titleIdFromWatchPath,
} from './googleAdSuitability.js';

const AD_CONFIG_ENDPOINT = '/api/watchvim-ad-config';

const DEFAULTS = Object.freeze({
  enabled: true,
  imaSdkUrl: 'https://imasdk.googleapis.com/js/sdkloader/ima3.js',
  prerollTag: '',
  midrollTag: '',
  houseTag: '/api/ads/house-vast',
  directCampaignsEnabled: true,
  prerollPodCount: 1,
  midrollPodCount: 3,
  midrollEveryMinutes: 22,
  midrollPodGapMs: 200,
  vastLoadTimeoutMs: 15000,
  minAdSeconds: 5,
  maxAdSeconds: 90,
  maxAdMs: 90000,
  maxPodMs: 380000,
  startMuted: false,
  adsense: {
    enabled: false,
    client: '',
    watchCompanionSlot: '',
    width: 300,
    height: 250,
  },
  campaignPackages: {
    currency: 'USD',
    '15': '200.00',
    '30': '300.00',
    impressions: { '15': 10000, '30': 10000 },
    maxCreativeSeconds: 90,
    selfServiceMaxSeconds: 30,
  },
  advertising: {},
});

// Google owns the single preroll. All three midroll positions belong to
// WatchVIM inventory, with WatchVIM House as the only no-fill fallback.
export const MIDROLL_SLOT_CONTRACT = Object.freeze([
  Object.freeze({ id: 'midroll-slot-1-watchvim', index: 0, owner: 'watchvim' }),
  Object.freeze({ id: 'midroll-slot-2-watchvim', index: 1, owner: 'watchvim' }),
  Object.freeze({ id: 'midroll-slot-3-watchvim', index: 2, owner: 'watchvim' }),
]);

let configPromise = null;

function bounded(value, fallback, min, max) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(max, Math.max(min, number));
}

function money(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number.toFixed(2) : fallback;
}

function safeCompanionUrl(value, { allowRelative = false } = {}) {
  const url = String(value || '').trim();
  if (!url) return '';
  if (allowRelative && url.startsWith('/') && !url.startsWith('//')) return url;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? url : '';
  } catch {
    return '';
  }
}

function normalizeCompanionPlacements(raw = []) {
  if (!Array.isArray(raw)) return [];
  return raw.slice(0, 100).map((placement) => {
    if (!placement || typeof placement !== 'object' || placement.creativeType !== 'display-image') return null;
    const imageUrl = safeCompanionUrl(placement.imageUrl, { allowRelative: true });
    if (!imageUrl) return null;
    const source = placement.source === 'paid' ? 'paid' : 'internal';
    const placementScope = placement.placementScope === 'title' ? 'title' : 'platform';
    return {
      id: String(placement.id || '').trim(),
      source,
      creativeType: 'display-image',
      imageUrl,
      clickUrl: safeCompanionUrl(placement.clickUrl, { allowRelative: true }),
      alt: String(placement.alt || '').trim().slice(0, 240),
      placementScope,
      targetTitleId: placementScope === 'title' ? String(placement.targetTitleId || '').trim() : '',
      priority: Math.floor(bounded(placement.priority, 0, -100000, 100000)),
    };
  }).filter(Boolean);
}

function normalizeAdvertising(raw = {}) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  return {
    title: String(raw.title || '').trim(),
    description: String(raw.description || '').trim(),
    // Legacy scheduling fields remain visible for diagnostics, but playback
    // always follows the canonical platform AVOD policy normalized below.
    midrollCount: bounded(raw.midrollCount, null, 1, 3),
    midrollEveryMins: bounded(raw.midrollEveryMins ?? raw.defaultAdFrequencyMins, null, 1, 120),
    midrollPodMaxMs: bounded(raw.midrollPodMaxMs, null, 10000, 380000),
    approvedAdPlacements: Array.isArray(raw.approvedAdPlacements) ? raw.approvedAdPlacements : [],
    companionPlacements: normalizeCompanionPlacements(raw.companionPlacements),
  };
}

function normalizePackages(raw = {}) {
  return {
    currency: String(raw.currency || DEFAULTS.campaignPackages.currency).trim().toUpperCase() || 'USD',
    '15': money(raw['15'], DEFAULTS.campaignPackages['15']),
    '30': money(raw['30'], DEFAULTS.campaignPackages['30']),
    impressions: {
      '15': Math.floor(bounded(raw?.impressions?.['15'], DEFAULTS.campaignPackages.impressions['15'], 1, 100000000)),
      '30': Math.floor(bounded(raw?.impressions?.['30'], DEFAULTS.campaignPackages.impressions['30'], 1, 100000000)),
    },
    maxCreativeSeconds: Math.floor(bounded(raw.maxCreativeSeconds, DEFAULTS.campaignPackages.maxCreativeSeconds, 5, 90)),
    selfServiceMaxSeconds: Math.floor(bounded(raw.selfServiceMaxSeconds, DEFAULTS.campaignPackages.selfServiceMaxSeconds, 1, 30)),
  };
}

function normalizeAdsense(raw = {}) {
  const client = String(raw.client || '').trim();
  const watchCompanionSlot = String(raw.watchCompanionSlot || '').trim();
  const candidatePath = String(raw.adUnitPath || '').trim();
  const adUnitPath = /^\/\d+\/[A-Za-z0-9_/-]+$/.test(candidatePath) ? candidatePath : '';
  return {
    enabled: raw.enabled !== false && (Boolean(adUnitPath) || (/^ca-pub-\d+$/i.test(client) && /^\d+$/.test(watchCompanionSlot))),
    adUnitPath,
    refreshDeclared: raw.refreshDeclared === true,
    client,
    watchCompanionSlot,
    width: Math.floor(bounded(raw.width, DEFAULTS.adsense.width, 1, 1200)),
    height: Math.floor(bounded(raw.height, DEFAULTS.adsense.height, 1, 1200)),
  };
}

function normalize(raw = {}) {
  return {
    enabled: raw.enabled !== false,
    imaSdkUrl: String(raw.imaSdkUrl || DEFAULTS.imaSdkUrl),
    prerollTag: String(raw.prerollTag || ''),
    midrollTag: String(raw.midrollTag || ''),
    houseTag: String(raw.houseTag || DEFAULTS.houseTag),
    directCampaignsEnabled: raw.directCampaignsEnabled !== false,
    prerollPodCount: Math.floor(bounded(raw.prerollPodCount, DEFAULTS.prerollPodCount, 1, 1)),
    midrollPodCount: Math.floor(bounded(raw.midrollPodCount, DEFAULTS.midrollPodCount, 3, 3)),
    midrollEveryMinutes: bounded(raw.midrollEveryMinutes, DEFAULTS.midrollEveryMinutes, 22, 22),
    midrollPodGapMs: Math.floor(bounded(raw.midrollPodGapMs, DEFAULTS.midrollPodGapMs, 0, 5000)),
    vastLoadTimeoutMs: Math.floor(bounded(raw.vastLoadTimeoutMs, DEFAULTS.vastLoadTimeoutMs, 3000, 15000)),
    minAdSeconds: Math.floor(bounded(raw.minAdSeconds, DEFAULTS.minAdSeconds, 5, 5)),
    maxAdSeconds: Math.floor(bounded(raw.maxAdSeconds, DEFAULTS.maxAdSeconds, 90, 90)),
    maxAdMs: Math.floor(bounded(raw.maxAdMs, DEFAULTS.maxAdMs, 90000, 90000)),
    maxPodMs: Math.floor(bounded(raw.maxPodMs, DEFAULTS.maxPodMs, 380000, 380000)),
    startMuted: raw.startMuted === true,
    adsense: normalizeAdsense(raw.adsense),
    campaignPackages: normalizePackages(raw.campaignPackages),
    advertising: normalizeAdvertising(raw.advertising),
  };
}

function applyCurrentTitleSuitability(config) {
  if (typeof window === 'undefined') return config;
  const titleId = titleIdFromWatchPath(window.location?.pathname || '');
  if (!titleId) return config;
  const suitability = googleAdSuitabilityForTitleId(titleId);
  if (suitability.googleDemandEligible) return config;
  return {
    ...config,
    adsense: { ...config.adsense, enabled: false },
    googleAdSuitability: suitability,
  };
}

export async function fetchAdConfig({ force = false } = {}) {
  if (!force && configPromise) return configPromise;
  const request = fetch(AD_CONFIG_ENDPOINT, {
    method: 'GET',
    headers: { accept: 'application/json' },
    cache: 'no-store',
  }).then(async (response) => {
    if (!response.ok) throw new Error(`AVOD config failed with ${response.status}`);
    return applyCurrentTitleSuitability(normalize(await response.json()));
  }).catch(() => applyCurrentTitleSuitability(normalize(DEFAULTS)));
  configPromise = request;
  request.finally(() => { if (configPromise === request) configPromise = null; });
  return request;
}

export function midrollPodCount(config) {
  return Math.floor(bounded(config?.midrollPodCount, DEFAULTS.midrollPodCount, 3, 3));
}

// CMS publishes adBreaks as seconds. Explicit cues also apply to short titles;
// the 22-minute / five-minute-tail rules only govern the fallback schedule.
export function midrollBreakpoints(durationSeconds, policy, config) {
  const duration = Number(durationSeconds);
  if (!Number.isFinite(duration) || duration <= 0) return [];
  const custom = policy?.raw?.adBreaks ?? policy?.adBreaks;
  if (Array.isArray(custom) && custom.length) {
    return [...new Set(custom.map((value) => {
      if (typeof value === 'number') return value;
      if (typeof value !== 'string' || !value.trim()) return NaN;
      const parts = value.trim().split(':');
      if (!parts.every((part) => /^\d+(?:\.\d+)?$/.test(part))) return NaN;
      if (parts.length > 3 || parts.slice(1).some((part) => Number(part) >= 60)) return NaN;
      return parts.reduce((seconds, part) => seconds * 60 + Number(part), 0);
    }).filter((point) => Number.isFinite(point) && point > 0 && point < duration))].sort((a, b) => a - b);
  }
  if (duration < 20 * 60) return [];
  const spacing = bounded(config?.midrollEveryMinutes, DEFAULTS.midrollEveryMinutes, 22, 22) * 60;
  const points = [];
  for (let at = spacing; at <= duration - 5 * 60; at += spacing) points.push(Math.round(at));
  return points;
}

export function directCampaignTag(titleId, position, { slot = 0, podSeed = 0 } = {}) {
  const id = encodeURIComponent(String(titleId || ''));
  const adPosition = String(position || 'preroll').toLowerCase() === 'midroll' ? 'midroll' : 'preroll';
  let path = `/api/ad-campaigns/vast?titleId=${id}&position=${adPosition}`;
  if (adPosition === 'midroll') {
    const normalizedSlot = Math.max(1, Math.min(3, Math.floor(Number(slot) || 1)));
    const normalizedSeed = Math.max(1, Math.floor(Number(podSeed) || Date.now()));
    path += `&slot=${normalizedSlot}&podSeed=${normalizedSeed}`;
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    try { return new URL(path, window.location.origin).toString(); } catch (_) {}
  }
  return path;
}

function unique(values) { return [...new Set(values.map((value) => String(value || '').trim()).filter(Boolean))]; }

function withTagParam(value, key, paramValue) {
  const tag = String(value || '').trim();
  if (!tag) return '';
  const pattern = new RegExp(`([?&])${key}=[^&]*`, 'i');
  if (pattern.test(tag)) return tag.replace(pattern, `$1${key}=${paramValue}`);
  return `${tag}${tag.includes('?') ? '&' : '?'}${key}=${paramValue}`;
}

function withGoogleVideoPosition(value, slot) {
  const tag = String(value || '').trim();
  if (!tag || !isGoogleDemandUrl(tag)) return tag;
  const vpos = slot === 'midroll' ? 'midroll' : 'preroll';
  // Each Google-owned midroll position requests one ad only so Google cannot
  // create a nested pod inside the WatchVIM three-position break.
  return [
    ['vpos', vpos],
    ['pmad', '1'],
    ['min_ad_duration', '5000'],
    ['max_ad_duration', '90000'],
  ].reduce((current, [key, paramValue]) => withTagParam(current, key, paramValue), tag);
}

export function midrollSlotPlan({ config, titleId, podSeed = Date.now() }) {
  const suitability = googleAdSuitabilityForTitleId(titleId);
  const normalizedPodSeed = Math.max(1, Math.floor(Number(podSeed) || Date.now()));

  return MIDROLL_SLOT_CONTRACT.map((slot) => {
    const slotNumber = slot.index + 1;
    const candidates = [];

    if (slot.owner === 'watchvim' && suitability.allowDirectAds) {
      if (config?.directCampaignsEnabled !== false) {
        candidates.push(directCampaignTag(titleId, 'midroll', { slot: slotNumber, podSeed: normalizedPodSeed }));
      }
      candidates.push(config?.houseTag);
    }

    return {
      ...slot,
      slotNumber,
      podSeed: normalizedPodSeed,
      candidates: unique(candidates),
      googleDemandEligible: suitability.googleDemandEligible,
      classification: suitability.classification || 'unreviewed',
    };
  });
}

export function adTagCandidates({ config, titleId, position, includeDirect = true, allowGoogle = true }) {
  const slot = String(position || 'preroll').toLowerCase() === 'midroll' ? 'midroll' : 'preroll';
  const primary = slot === 'midroll' ? config?.midrollTag : config?.prerollTag;
  const suitability = googleAdSuitabilityForTitleId(titleId);
  const values = [];

  if (slot === 'midroll') {
    // Backward-compatible candidate access for legacy callers. Canonical V3
    // midroll playback uses midrollSlotPlan() and its explicit source owners.
    const plan = midrollSlotPlan({ config, titleId });
    if (includeDirect) return [...plan[0].candidates];
    return plan[0].candidates.filter((tag) => !/\/api\/ad-campaigns\/vast(?:[/?]|$)/i.test(tag));
  }

  // Declining Google advertising technologies does not make AVOD ad-free.
  // Use only the publisher's configured house inventory in that case.
  if (!allowGoogle) {
    return suitability.allowDirectAds && config?.houseTag && !isGoogleDemandUrl(config.houseTag)
      ? unique([config.houseTag]) : [];
  }
  // With advertising permission, the preroll remains Google-owned.
  // No Google fill or an ineligible title skips the slot and resumes content.
  if (primary && suitability.googleDemandEligible && isGoogleDemandUrl(primary)) {
    values.push(withGoogleVideoPosition(primary, slot));
  }
  return unique(values);
}
